const MAX_ABS_DEFAULT = 10000000;

const splitIfSingleCommaString = (validValues = []) => {
  if (Array.isArray(validValues)) {
    if (validValues.length === 1 && typeof validValues[0] === "string" && validValues[0].includes(",")) {
      return validValues[0].split(",").map((v) => v.trim()).filter(Boolean);
    }
    return validValues.map((v) => (typeof v === "string" ? v.trim() : v));
  }
  if (typeof validValues === "string") {
    const trimmed = validValues.trim();
    if (!trimmed) return [];
    if (trimmed.includes(",")) {
      return trimmed.split(",").map((v) => v.trim()).filter(Boolean);
    }
    return [trimmed];
  }
  return [];
};

export const deriveParamMeta = (def = {}) => {
  const rawVals = splitIfSingleCommaString(def.validValues || []);
  const lowerVals = rawVals.map((v) => (typeof v === "string" ? v.toLowerCase() : v));
  const str = lowerVals.join(",");

  // Numeric constraints
  if (str === ">0") {
    return {
      inputType: "number",
      minExclusive: 0,
      maxAbs: MAX_ABS_DEFAULT,
      hint: "Must be greater than 0",
      castValue: (v) => (v === "" || v === null || v === undefined ? "" : Number(v)),
    };
  }
  if (str === ">=0" || str === ">= 0" ) {
    return {
      inputType: "number",
      minInclusive: 0,
      maxAbs: MAX_ABS_DEFAULT,
      hint: "Must be zero or greater",
      castValue: (v) => (v === "" || v === null || v === undefined ? "" : Number(v)),
    };
  }
  if (str === "1/0" || str === "y/n" || str === "y,n" || str === "yes/no") {
    const isYN = str.includes("y");
    return {
      inputType: "select",
      options: [
        { label: "Yes", value: isYN ? "Y" : "1" },
        { label: "No", value: isYN ? "N" : "0" },
      ],
      castValue: (v) => (v === "" || v === null || v === undefined ? "" : String(v)),
    };
  }

  // Generic CSV/array dropdown fallback (takes precedence over dataType)
  if (rawVals.length > 0) {
    return {
      inputType: "select",
      options: rawVals,
      castValue: (v) => (v === "" || v === null || v === undefined ? "" : String(v)),
    };
  }

  // Fallback based on dataType
  if (def.dataType === "INT" || def.dataType === "FLOAT") {
    return {
      inputType: "number",
      maxAbs: MAX_ABS_DEFAULT,
      hint: "Enter number",
      castValue: (v) => (v === "" || v === null || v === undefined ? "" : Number(v)),
    };
  }
  if (def.dataType === "BOOL") {
    return {
      inputType: "select",
      options: ["1", "0"],
      castValue: (v) => (v === "" || v === null || v === undefined ? "" : String(v)),
    };
  }

  // Default text with hint if provided
  return {
    inputType: "text",
    hint: typeof def.validValues === "string" && def.validValues.trim() ? def.validValues.trim() : "Enter text",
  };
};

export const validateParamValue = (meta, value) => {
  if (value === null || value === undefined || value === "") return false;

  // Options
  if (meta.inputType === "select" && Array.isArray(meta.options) && meta.options.length) {
    const optionValues = meta.options.map((opt) =>
      typeof opt === "object" && opt !== null && Object.prototype.hasOwnProperty.call(opt, "value")
        ? opt.value
        : opt
    );
    return optionValues.map(String).includes(String(value));
  }

  // Numeric checks
  if (meta.inputType === "number") {
    const num = Number(value);
    if (Number.isNaN(num)) return false;
    if (meta.minExclusive !== undefined && !(num > meta.minExclusive)) return false;
    if (meta.minInclusive !== undefined && !(num >= meta.minInclusive)) return false;
    if (meta.maxAbs !== undefined && Math.abs(num) > meta.maxAbs) return false;
  }

  return true;
};

export const formatParamName = (name) => {
  if (!name) return "";
  const src = String(name).trim();
  if (!src) return "";

  const words = [];
  let i = 0;

  const isUpper = (ch) => ch >= "A" && ch <= "Z";

  while (i < src.length) {
    if (src.startsWith("ADX", i)) {
      words.push("ADX");
      i += 3;
      continue;
    }
    if (src.startsWith("AutoX", i)) {
      words.push("AutoX");
      i += 5;
      continue;
    }

    let word = src[i];
    i += 1;

    while (i < src.length) {
      const curr = src[i];
      const prev = src[i - 1];
      if (isUpper(curr) && prev && !isUpper(prev)) break;
      word += curr;
      i += 1;
    }

    words.push(word);
  }

  return words.join(" ");
};